import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { OperatorMetricsDto, PauseHistoryDto, PeakHourDto, QueueGroupSummaryDto, SupervisorStatusDto, TicketWindowLockDto, SessionEventDto, PasswordTrailSummaryDto, PasswordTrailEventDto } from '../../interfaces/supervisor-status.dto';

@Injectable({ providedIn: 'root' })
export class SupervisorService {
  private readonly apiURL = `${environment.apiUrl}/v${environment.apiVersion}/supervisor`;

  constructor(private http: HttpClient) {}

  async getLiveStatus(clinicId: number): Promise<SupervisorStatusDto[]> {
    try {
      const response = await firstValueFrom(this.http.get<any>(`${this.apiURL}/${clinicId}/live`));
      return response?.data ?? [];
    } catch (e) { console.error('[Supervisor] live:', e); return []; }
  }

  async getMetrics(clinicId: number, dateRef?: string): Promise<OperatorMetricsDto[]> {
    const date = dateRef ?? new Date().toISOString().split('T')[0];
    try {
      const response = await firstValueFrom(this.http.get<any>(`${this.apiURL}/${clinicId}/metrics?dateRef=${date}`));
      return response?.data ?? [];
    } catch (e) { console.error('[Supervisor] metrics:', e); return []; }
  }

  async getPeakHours(clinicId: number, dateRef?: string): Promise<PeakHourDto[]> {
    const date = dateRef ?? new Date().toISOString().split('T')[0];
    try {
      const response = await firstValueFrom(this.http.get<any>(`${this.apiURL}/${clinicId}/peak-hours?dateRef=${date}`));
      return response?.data ?? [];
    } catch (e) { console.error('[Supervisor] peak-hours:', e); return []; }
  }

  async getQueueSummary(clinicId: number): Promise<QueueGroupSummaryDto[]> {
    try {
      const response = await firstValueFrom(this.http.get<any>(`${this.apiURL}/${clinicId}/queue-summary`));
      return response?.data ?? [];
    } catch (e) { console.error('[Supervisor] queue-summary:', e); return []; }
  }

  async getPauseHistory(clinicId: number, dateRef?: string): Promise<PauseHistoryDto[]> {
    const date = dateRef ?? new Date().toISOString().split('T')[0];
    try {
      const response = await firstValueFrom(this.http.get<any>(`${this.apiURL}/${clinicId}/pause-history?dateRef=${date}`));
      return response?.data ?? [];
    } catch (e) { console.error('[Supervisor] pause-history:', e); return []; }
  }

  async getSessionHistory(clinicId: number, dateRef?: string): Promise<SessionEventDto[]> {
    const date = dateRef ?? new Date().toISOString().split('T')[0];
    try {
      const response = await firstValueFrom(this.http.get<any>(`${this.apiURL}/${clinicId}/session-history?dateRef=${date}`));
      return response?.data ?? [];
    } catch (e) { console.error('[Supervisor] session-history:', e); return []; }
  }

  async getPasswordTrail(clinicId: number, dateRef?: string): Promise<PasswordTrailSummaryDto[]> {
    const date = dateRef ?? new Date().toISOString().split('T')[0];
    try {
      const response = await firstValueFrom(this.http.get<any>(`${this.apiURL}/${clinicId}/password-trail?dateRef=${date}`));
      return response?.data ?? [];
    } catch (e) { console.error('[Supervisor] password-trail:', e); return []; }
  }

  async getPasswordTimeline(clinicId: number, passwordId: number): Promise<PasswordTrailEventDto[]> {
    try {
      const response = await firstValueFrom(this.http.get<any>(`${this.apiURL}/${clinicId}/password-trail/${passwordId}`));
      return response?.data ?? [];
    } catch (e) { console.error('[Supervisor] password-timeline:', e); return []; }
  }

  async getTicketWindows(clinicId: number): Promise<TicketWindowLockDto[]> {
    try {
      const response = await firstValueFrom(this.http.get<any>(`${this.apiURL}/${clinicId}/ticket-windows`));
      return response?.data ?? [];
    } catch (e) { console.error('[Supervisor] ticket-windows:', e); return []; }
  }

  /** Zera a reserva do guiche, devolvendo-o para a lista de guiches disponiveis no login. */
  async releaseTicketWindow(clinicId: number, ticketWindowName: string): Promise<string> {
    const response = await firstValueFrom(
      this.http.put<any>(`${this.apiURL}/${clinicId}/ticket-windows/release`, { ticketWindowName })
    );
    return response?.data?.[0] ?? response?.feedback ?? 'Guichê liberado.';
  }

  async getOperators(clinicId: number): Promise<string[]> {
    try {
      const response = await firstValueFrom(this.http.get<any>(`${this.apiURL}/${clinicId}/operators`));
      return response?.data ?? [];
    } catch (e) { console.error('[Supervisor] operators:', e); return []; }
  }
}
